import * as Sentry from "@sentry/nextjs"
import { Accordion, Badge, Button, Spinner, Textarea } from "flowbite-react"
import { FieldArray, Form, FormikHelpers, FormikProps, FormikProvider, useFormik } from "formik"
import React, { ReactNode, useEffect, useMemo, useState } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import { DownIcon, LeftSmallIcon } from "@assets/icons"
import FormGroup from "@components/elements/FormGroup"
import { useDebounce } from "@hooks/useDebounce"
import useToast from "@hooks/useToast"
import InfoIcon from "@icons/Info"
import EmptyItems from "@modules/tenants/OrdersPage/Components/EmptyItems"
import { ASK_PER_BOOKING, ASK_PER_PARTICIPANT } from "@mono/essentials/constants/options"
import { addonDetailsType } from "@mono/essentials/entities/cart/types"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductPricingServices from "@mono/essentials/services/ProductPricingServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import {
  ProductFieldRulesType,
  ProductFieldType,
  ProductType,
} from "@mono/essentials/types/OrderTypes"
import { ProductPricingType } from "@mono/essentials/types/ProductPricingTypes"
import { ProductAddonType } from "@mono/essentials/types/ProductTypes"

import { ProductInfoFormAttributes } from "../ProductInfoForm/ProductInfoForm"

import ParticipantsFields from "./ParticipantsFields"

export const checkDisplayRules = ({
  field,
  formValues,
  ticketInfo,
  isDontShowForBookingEnabled = true,
}: {
  field: ProductFieldType
  formValues?: Record<string, string | number | boolean | string[]>
  ticketInfo?: ProductInfoFormAttributes["units"]
  // For Checklist feature, some fields (per_order & per_booking) are hidden during order creation and shown only in post order process.
  isDontShowForBookingEnabled?: boolean
}) => {
  const handleNestedRules = (ruleWithCondition: ProductFieldRulesType["rules"][0]["rules"][0]) => {
    const ruleConditionsResults: boolean[] = []
    ruleWithCondition?.rules?.forEach((rule) => {
      // Check for the nested grouped rules
      if (rule?.condition && rule?.rules?.length) {
        const result = handleNestedRules(rule)
        ruleConditionsResults.push(result)
      } else {
        if (rule.type === "ticket") {
          let conditionResult = false
          ticketInfo?.every((unit) => {
            if (rule.operator === "is") {
              if (unit.unitId === rule.value && unit.unitCount > 0) {
                conditionResult = true
                return false
              }
              return true
            } else if (rule.operator === "is_not") {
              if (unit.unitId !== rule.value) {
                conditionResult = true
                return true
              }
              conditionResult = !(unit.unitCount > 0)
              return !(unit.unitCount > 0)
            }
            // If there are anyother conditions other than is & is_not, it is bypassed as valid.
            conditionResult = true
            return true
          })
          ruleConditionsResults.push(conditionResult)
        } else if (rule.type === "field") {
          let conditionResult = false
          Object.keys(formValues ?? {})
            .filter((fieldKey) => fieldKey.includes("_id"))
            .every((fieldIdKey) => {
              if (formValues?.[fieldIdKey] === rule.product_field_id) {
                const fieldValueKey = fieldIdKey.split("_id")?.[0]
                if (rule.operator === "is") {
                  conditionResult = Array.isArray(formValues[fieldValueKey])
                    ? (formValues[fieldValueKey] as string[]).includes(rule.value)
                    : formValues[fieldValueKey] === rule.value
                } else if (rule.operator === "is_not") {
                  conditionResult = Array.isArray(formValues[fieldValueKey])
                    ? !(formValues[fieldValueKey] as string[]).includes(rule.value)
                    : formValues[fieldValueKey] !== rule.value
                } else {
                  // If there are anyother conditions other than is & is_not, it is bypassed as valid.
                  conditionResult = true
                }
                return false
              }
              return true
            })
          ruleConditionsResults.push(conditionResult)
        } else {
          // If the rule takes inputs others than the field and ticket, it's bypassed as valid.
          ruleConditionsResults.push(true)
        }
      }
    })

    // If the operator is other than AND & OR, the outcome is made valid by default.
    if (ruleWithCondition?.condition === "AND") {
      return ruleConditionsResults.reduce((prevValue, currentValue) => prevValue && currentValue)
    } else if (ruleWithCondition?.condition === "OR") {
      return ruleConditionsResults.reduce((prevValue, currentValue) => prevValue || currentValue)
    }
    return true
  }
  if (isDontShowForBookingEnabled && field.dont_show_during_order === true) {
    return "hide"
  }
  if (field?.rules?.length) {
    const outcome = field.rules[0].is_hidden ? "hide" : "show"
    if (field.rules[0].rules?.length) {
      // rulesOutcomesResults holds the computated result of all the outcomes. Example: { show: true, hide: false }
      const rulesOutcomesResults: Record<string, boolean> = {}
      field.rules[0].rules.forEach((ruleWithOutcome) => {
        if (ruleWithOutcome.condition && ruleWithOutcome.outcome) {
          if (ruleWithOutcome?.rules?.length) {
            // ruleConditionsResults holds the result of each condition in the rule
            const ruleConditionsResults: boolean[] = []
            ruleWithOutcome?.rules?.forEach((rule) => {
              // Check for the nested grouped rules
              if (rule?.condition && rule?.rules?.length) {
                const result = handleNestedRules(rule)
                ruleConditionsResults.push(result)
              } else {
                if (rule.type === "ticket") {
                  let conditionResult = false
                  ticketInfo?.every((unit) => {
                    if (rule.operator === "is") {
                      if (unit.unitId === rule.value && unit.unitCount > 0) {
                        conditionResult = true
                        return false
                      }
                      return true
                    } else if (rule.operator === "is_not") {
                      if (unit.unitId !== rule.value) {
                        conditionResult = true
                        return true
                      }
                      conditionResult = !(unit.unitCount > 0)
                      return !(unit.unitCount > 0)
                    }
                    // If there are anyother conditions other than is & is_not, it is bypassed as valid.
                    conditionResult = true
                    return true
                  })
                  ruleConditionsResults.push(conditionResult)
                } else if (rule.type === "field") {
                  let conditionResult = false
                  Object.keys(formValues ?? {})
                    .filter((fieldKey) => fieldKey.includes("_id"))
                    .every((fieldIdKey) => {
                      if (formValues?.[fieldIdKey] === rule.product_field_id) {
                        const fieldValueKey = fieldIdKey.split("_id")?.[0]
                        if (rule.operator === "is") {
                          // For list field type, the value is array of string
                          conditionResult = Array.isArray(formValues[fieldValueKey])
                            ? (formValues[fieldValueKey] as string[]).includes(rule.value)
                            : formValues[fieldValueKey] === rule.value
                        } else if (rule.operator === "is_not") {
                          conditionResult = Array.isArray(formValues[fieldValueKey])
                            ? !(formValues[fieldValueKey] as string[]).includes(rule.value)
                            : formValues[fieldValueKey] !== rule.value
                        } else {
                          // If there are anyother conditions other than is & is_not, it is bypassed as valid.
                          conditionResult = true
                        }
                        return false
                      }
                      return true
                    })
                  ruleConditionsResults.push(conditionResult)
                } else {
                  // If the rule takes inputs others than the field and ticket, it's bypassed as valid.
                  ruleConditionsResults.push(true)
                }
              }
            })

            // If the operator is other than AND & OR, the outcome is made valid by default.
            if (ruleWithOutcome?.condition === "AND") {
              rulesOutcomesResults[ruleWithOutcome.outcome] = ruleConditionsResults.reduce(
                (prevValue, currentValue) => prevValue && currentValue
              )
            } else if (ruleWithOutcome?.condition === "OR") {
              rulesOutcomesResults[ruleWithOutcome.outcome] = ruleConditionsResults.reduce(
                (prevValue, currentValue) => prevValue || currentValue
              )
            } else {
              rulesOutcomesResults[ruleWithOutcome.outcome] = true
            }
          } else {
            rulesOutcomesResults[ruleWithOutcome.outcome] = true
          }
        }
      })

      const validatedOutcomes = Object.keys(rulesOutcomesResults).filter(
        (outcome) => rulesOutcomesResults[outcome]
      )
      return validatedOutcomes?.length ? validatedOutcomes[0] : outcome
    }
    return outcome
  }
  return "show"
}

export const checkAddonDisplayRules = ({
  addon,
  skipAddonDisplayRules,
  perBookingFields,
  perParticipantFields,
  isDontShowForBookingEnabled,
  ticketInfo,
}: {
  addon: ProductAddonType
  skipAddonDisplayRules: boolean
  perBookingFields?: Record<string, string | number | boolean | string[]>
  perParticipantFields?: Record<string, string | number | boolean | string[]>[]
  ticketInfo?: ProductInfoFormAttributes["units"]
  // For Checklist feature, some fields (per_order & per_booking) are hidden during order creation and shown only in post order process.
  isDontShowForBookingEnabled?: boolean
}) => {
  if (skipAddonDisplayRules) return true
  if (addon.merchandise.availability === "customer") {
    // For customer type addon, only check the rules with per-booking fields.
    return (
      checkDisplayRules({
        field: addon as unknown as ProductFieldType,
        formValues: perBookingFields,
        ticketInfo,
        isDontShowForBookingEnabled,
      }) === "show"
    )
  }
  if (addon.merchandise.availability === "participant") {
    // For participant type addon, only check the rules with per-participant fields.
    let displayAddon = false
    perParticipantFields?.forEach((participant) => {
      if (
        checkDisplayRules({
          field: addon as unknown as ProductFieldType,
          formValues: participant,
          ticketInfo,
          isDontShowForBookingEnabled,
        }) === "show"
      ) {
        displayAddon = true
        return true
      }
    })
    return displayAddon
  }
}

export type ParticipationDetailsFormAttributes = {
  internalNotes: string
  addonDetails?: addonDetailsType[]
  participantsDetails?: Record<string, boolean | string | string[] | number>[]
  perBookingFieldsDetails?: Record<string, boolean | string | string[] | number>[]
  addonPricing?: any
}
export interface ParticipationDetailsFormProps {
  productInfo: ProductInfoFormAttributes
  onSuccess?: (arg0: ParticipationDetailsFormAttributes) => Promise<void>
  error?: string
  handleClose: () => void
  goToBackStep?: () => void
  selectedProduct?: ProductType
  formInitialValues?: ParticipationDetailsFormAttributes
  hideBackButton?: boolean
  selectedProductId?: string
  hideDefaultContainer?: boolean
  participantDetailsRef?: React.RefObject<FormikProps<ParticipationDetailsFormAttributes>>
  isIntegratedPage?: boolean
  isWidgetpage?: boolean
  focusedParticipantId?: string
  hideParticipantFields?: boolean
  hidePerBookingFields?: boolean
  hideAddonsFields?: boolean
  hideNoteField?: boolean
  alertMessage?: string | ReactNode
  isPostOrder?: boolean
  skipAddonDisplayRules?: boolean
}

const ParticipationDetailsForm = ({
  onSuccess,
  handleClose,
  goToBackStep,
  productInfo,
  selectedProduct,
  formInitialValues,
  selectedProductId,
  hideBackButton = false,
  hideDefaultContainer = false,
  participantDetailsRef,
  isIntegratedPage = false,
  isWidgetpage = false,
  error,
  focusedParticipantId,
  hideParticipantFields = false,
  hidePerBookingFields = false,
  hideAddonsFields = false,
  hideNoteField = false,
  alertMessage,
  isPostOrder = false,
  skipAddonDisplayRules = false,
}: ParticipationDetailsFormProps) => {
  const { showToast } = useToast()
  const [productAddonsPriceDataPayload, setProductAddonsPriceDataPayload] = useState<string[]>()
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [addonPrices, setAddOnPrices] = useState(formInitialValues?.addonPricing ?? {})
  const [debounceParams, setDebounceParams] = useState({})
  const debounced = useDebounce(debounceParams, 500)

  const {
    data: productFieldsData,
    isLoading: isProductFieldsDataLoading,
    error: productFieldsDataError,
  } = useSWR(
    selectedProduct?.id || selectedProductId
      ? `productFieldsData-${selectedProduct?.id || selectedProductId}`
      : null,
    async () => {
      const selectProductId = selectedProductId || selectedProduct?.id || ""
      const { data: getProductFieldsResponse } = await OrderServices.getProductFields(
        selectProductId,
        isIntegratedPage
      )
      if (getProductFieldsResponse) {
        return getProductFieldsResponse.data
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const { data: productAddonsData, isLoading: isProductAddonsDataLoading } = useSWR(
    selectedProduct?.id || selectedProductId
      ? `productAddonsData-${selectedProduct?.id || selectedProductId}`
      : null,
    async () => {
      const selectProductId = selectedProductId || selectedProduct?.id || ""
      const { data: getProductFieldsResponse } = await ProductServices.getProductAddons(
        selectProductId,
        false,
        {
          includeRules: true,
          paginate: "0",
        }
      )
      if (getProductFieldsResponse) {
        return getProductFieldsResponse
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const { data: productAddonsPriceData } = useSWR(
    productAddonsPriceDataPayload?.length
      ? `productAddonsPriceData-${selectedProduct?.id || selectedProductId}`
      : null,
    async () => {
      const allAddonsPriceDataResponse: Record<string, ProductPricingType[]> = {}
      if (productAddonsPriceDataPayload)
        await Promise.all(
          productAddonsPriceDataPayload.map((addonId) =>
            ProductPricingServices.getProductPricingByProdId(addonId, {}, false, "").then(
              (resp) => {
                if (addonId && resp?.data?.data)
                  allAddonsPriceDataResponse[addonId] = resp.data.data
              }
            )
          )
        )
      if (Object.keys(allAddonsPriceDataResponse).length) {
        return allAddonsPriceDataResponse
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  useEffect(() => {
    if ((productAddonsData as unknown as ProductAddonType[])?.length) {
      setProductAddonsPriceDataPayload(() =>
        (productAddonsData as unknown as ProductAddonType[]).map((addon) => addon.id)
      )
    }
  }, [productAddonsData])

  const initialValues = useMemo<ParticipationDetailsFormAttributes>(() => {
    const participantsFieldsArray: Record<string, boolean | string | string[] | number>[] = []
    const perBookingFieldsArray: Record<string, boolean | string | string[] | number>[] = []
    if (productFieldsData) {
      productInfo.units.forEach((participantGroup) => {
        Array(parseInt(`${participantGroup.unitCount ?? 0}`, 10))
          .fill(1)
          .forEach((_, index) => {
            const fields: Record<string, boolean | string | string[] | number> = {}
            productFieldsData.forEach((productField) => {
              if (
                (isWidgetpage
                  ? productField.allowed_online_booking
                  : productField.allowed_internal_booking) &&
                productField.ask?.includes(ASK_PER_PARTICIPANT)
              ) {
                fields[productField.field.name] = productField.field.type === "checkbox" ? [] : ""
                fields[`${productField.field.name}_id`] = productField.id ?? ""
              }
            })
            fields.participantGroupName = participantGroup?.unitName
              ? `${participantGroup.unitName} ${participantGroup.unitCount > 1 ? index + 1 : ""}`
              : "Participant"
            fields.participantUnitId = participantGroup.unitId ?? ""
            participantsFieldsArray.push(fields)
          })
      })

      // Per booking fields
      const perBookingFields: Record<string, boolean | string | string[] | number> = {}
      productFieldsData
        .filter(
          (field) =>
            (isWidgetpage ? field.allowed_online_booking : field.allowed_internal_booking) &&
            field.ask?.includes(ASK_PER_BOOKING)
        )
        .forEach((productField) => {
          perBookingFields[productField.field.name] =
            productField.field.type === "checkbox" ? [] : ""
          perBookingFields[`${productField.field.name}_id`] = productField.id ?? ""
        })
      if (Object.keys(perBookingFields).length) perBookingFieldsArray.push(perBookingFields)
    }

    return {
      internalNotes: formInitialValues?.internalNotes ?? "",
      addonDetails: formInitialValues?.addonDetails ?? [],
      participantsDetails: formInitialValues?.participantsDetails
        ? formInitialValues.participantsDetails.map((pf, index) => ({
            ...pf,
            participantIndex: index,
          }))
        : participantsFieldsArray?.map((pf, index) => ({ ...pf, participantIndex: index })),
      perBookingFieldsDetails: formInitialValues?.perBookingFieldsDetails?.length
        ? formInitialValues.perBookingFieldsDetails
        : perBookingFieldsArray,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFieldsData])

  const onSubmit = async (
    values: ParticipationDetailsFormAttributes,
    { setFieldError, resetForm, setFieldValue }: FormikHelpers<ParticipationDetailsFormAttributes>
  ) => {
    try {
      setFormSubmitting(true)
      let isFormValidated = true
      // Check for mandatory add-ons
      let clonedAddonDetails = values.addonDetails?.slice()
      if (!skipAddonDisplayRules) {
        ;(productAddonsData as unknown as ProductAddonType[])
          .filter((addon) =>
            checkAddonDisplayRules({
              addon,
              skipAddonDisplayRules,
              isDontShowForBookingEnabled: !isPostOrder,
              perBookingFields: values?.perBookingFieldsDetails?.[0],
              perParticipantFields: values.participantsDetails,
              ticketInfo: productInfo?.units,
            })
          )
          .forEach((addon) => {
            if (addon?.merchandise?.requirements === "required" && clonedAddonDetails) {
              const enteredAddonDetailsIndex = clonedAddonDetails.findIndex(
                (ao) => ao.addonId === addon.id
              )
              if (enteredAddonDetailsIndex > -1) {
                if (clonedAddonDetails[enteredAddonDetailsIndex].requirement === "required") {
                  if (clonedAddonDetails[enteredAddonDetailsIndex].availability === "customer") {
                    let requestedAddonsCount = 0
                    const failedUnits: string[] = []
                    const availableUnits = productAddonsPriceData?.[addon.id] || []
                    const selectedUnits = clonedAddonDetails[enteredAddonDetailsIndex].units || []

                    availableUnits.forEach((unitPricingData) => {
                      const selectedUnitCount =
                        selectedUnits.find((unit: any) => unit.unitId === unitPricingData.unit?.id)
                          ?.unitCount || 0
                      requestedAddonsCount += selectedUnitCount

                      if (
                        unitPricingData?.unit?.min_quantity &&
                        selectedUnitCount < unitPricingData.unit.min_quantity
                      ) {
                        failedUnits.push(
                          `Minimum ${unitPricingData.unit.min_quantity} ${unitPricingData.unit.name}`
                        )
                      }
                    })

                    if (requestedAddonsCount === 0) {
                      isFormValidated = false
                      clonedAddonDetails[enteredAddonDetailsIndex].error =
                        "Add-ons is compulsory. Please select at-least one add-on."
                    } else if (failedUnits.length > 0) {
                      isFormValidated = false
                      clonedAddonDetails[enteredAddonDetailsIndex].error =
                        `${failedUnits.join(" and ")} required for ${addon.name}`
                    } else {
                      clonedAddonDetails[enteredAddonDetailsIndex].error = ""
                    }
                  } else {
                    const numberOfEditedAddons = clonedAddonDetails[
                      enteredAddonDetailsIndex
                    ].units?.filter((unit) => Boolean(unit.participantId))?.length

                    const failedParticipantsErrorMessage: string[] = []
                    values.participantsDetails
                      ?.filter((participant) =>
                        addon.merchandise.availability === "customer"
                          ? true
                          : checkDisplayRules({
                              field: addon as unknown as ProductFieldType,
                              formValues: participant,
                              ticketInfo: productInfo?.units,
                              isDontShowForBookingEnabled: !isPostOrder,
                            }) === "show"
                      )
                      ?.forEach((participant) => {
                        let requestedAddonsCount = 0
                        const failedUnits: string[] = []
                        const availableUnits = productAddonsPriceData?.[addon.id] || []
                        const selectedUnits = values.addonDetails
                          ?.find((participantAddon) => participantAddon.addonId === addon.id)
                          ?.units?.filter(
                            (participantAddonUnit) =>
                              participantAddonUnit[
                                numberOfEditedAddons ===
                                clonedAddonDetails?.[enteredAddonDetailsIndex]?.units?.length
                                  ? "participantId"
                                  : "participantGroupName"
                              ] ===
                              participant[
                                numberOfEditedAddons ===
                                clonedAddonDetails?.[enteredAddonDetailsIndex]?.units?.length
                                  ? "participantId"
                                  : "participantGroupName"
                              ]
                          )

                        availableUnits.forEach((unitPricingData) => {
                          const selectedUnitCount =
                            selectedUnits?.find((unit) => unit.unitId === unitPricingData.unit?.id)
                              ?.unitCount || 0
                          requestedAddonsCount += selectedUnitCount

                          if (
                            unitPricingData?.unit?.min_quantity &&
                            selectedUnitCount < unitPricingData.unit.min_quantity
                          ) {
                            failedUnits.push(
                              `Minimum ${unitPricingData.unit.min_quantity} ${unitPricingData.unit.name}`
                            )
                          }
                        })

                        if (requestedAddonsCount === 0) {
                          failedParticipantsErrorMessage.push(
                            `${participant?.participantGroupName ? `${participant?.participantGroupName}: ` : ""}Add-ons is compulsory. Please select at-least one add-on.`
                          )
                        } else if (failedUnits.length > 0 && clonedAddonDetails) {
                          failedParticipantsErrorMessage.push(
                            `${participant?.participantGroupName ? `${participant?.participantGroupName}: ` : ""}${failedUnits.join(" and ")} required for ${addon.name}`
                          )
                        }
                      })

                    if (failedParticipantsErrorMessage.length) {
                      isFormValidated = false
                      clonedAddonDetails[enteredAddonDetailsIndex].error =
                        failedParticipantsErrorMessage.join(" \n ")
                    } else {
                      clonedAddonDetails[enteredAddonDetailsIndex].error = ""
                    }
                  }
                }
              } else {
                // If a mandatory addon is not found in the values, push it to the values with error
                isFormValidated = false
                clonedAddonDetails = [
                  ...clonedAddonDetails,
                  {
                    addonId: addon.id,
                    error:
                      addon.merchandise.availability === "participant"
                        ? "Add-on is compulsory for every participant. Please select at-least one add-on for every participant."
                        : "Add-on is mandatory based on your selection!",
                    availability: addon.merchandise.availability,
                    requirement: addon.merchandise.requirements,
                    units: [],
                  } as addonDetailsType,
                ]
              }
            }
          })
      }
      setFieldValue("addonDetails", clonedAddonDetails)
      if (isFormValidated || hideAddonsFields) {
        await onSuccess?.(values)
        resetForm()
      }
    } catch (e: any) {
      Sentry.captureException(e)
      const errors = e?.response?.data?.errors || []
      // eslint-disable-next-line no-restricted-syntax
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
      }
      resetForm()
    } finally {
      setFormSubmitting(false)
    }
  }

  const participationDetailsForm = useFormik({
    initialValues,
    validationSchema: () =>
      Yup.lazy((formValues: ParticipationDetailsFormAttributes) => {
        if (productFieldsData) {
          const perBookingFields: Record<string, any> = {}
          const perParticipantFields: Record<string, any> = {}
          productFieldsData.forEach((productField) => {
            if (
              (isWidgetpage
                ? productField.allowed_online_booking
                : productField.allowed_internal_booking) &&
              productField.ask?.includes(ASK_PER_PARTICIPANT)
            ) {
              if (productField.field.type === "checkbox") {
                perParticipantFields[productField.field.name] = Yup.array()
                  .of(Yup.string())
                  .when([], {
                    is: (participantIndex: number) => {
                      return (
                        (isWidgetpage
                          ? productField.required_for_online_booking
                          : productField.required_for_internal_booking) &&
                        checkDisplayRules({
                          field: productField,
                          formValues: formValues?.participantsDetails?.[participantIndex],
                          ticketInfo: productInfo?.units,
                          isDontShowForBookingEnabled: !isPostOrder,
                        }) === "show"
                      )
                    },
                    then: (schema) =>
                      schema
                        .required(`*${productField.label} Required`)
                        .min(1, "*Atleast one is Required"),
                    otherwise: (schema) => schema.optional(),
                  })
              } else if (productField.field.type === "inline") {
                perParticipantFields[productField.field.name] = Yup.string().optional()
              } else {
                perParticipantFields[productField.field.name] = Yup.string().when(
                  ["participantIndex"],
                  {
                    is: (participantIndex: number) => {
                      return (
                        (isWidgetpage
                          ? productField.required_for_online_booking
                          : productField.required_for_internal_booking) &&
                        checkDisplayRules({
                          field: productField,
                          formValues: formValues?.participantsDetails?.[participantIndex],
                          ticketInfo: productInfo?.units,
                          isDontShowForBookingEnabled: !isPostOrder,
                        }) === "show"
                      )
                    },
                    then: (schema) => {
                      if (productField.field.name === "email") {
                        return schema
                          .email("*Invalid email")
                          .required(`*${productField.label} Required`)
                      }
                      return schema.required(`*${productField.label} Required`)
                    },
                    otherwise: (schema) => {
                      if (productField.field.name === "email") {
                        return schema.email("*Invalid email").optional()
                      }
                      return schema.optional()
                    },
                  }
                )
              }
            } else if (
              (isWidgetpage
                ? productField.allowed_online_booking
                : productField.allowed_internal_booking) &&
              productField.ask?.includes(ASK_PER_BOOKING) &&
              checkDisplayRules({
                field: productField,
                formValues: formValues?.perBookingFieldsDetails?.[0],
                ticketInfo: productInfo?.units,
                isDontShowForBookingEnabled: !isPostOrder,
              }) === "show"
            ) {
              if (productField.field.type === "checkbox") {
                perBookingFields[productField.field.name] = (
                  isWidgetpage
                    ? productField.required_for_online_booking
                    : productField.required_for_internal_booking
                )
                  ? Yup.array()
                      .of(Yup.string())
                      .required(`*${productField.label} Required`)
                      .min(1, "*Atleast one is Required")
                  : Yup.array().of(Yup.string())
              } else if (productField.field.type === "inline") {
                perBookingFields[productField.field.name] = Yup.string().optional()
              } else {
                perBookingFields[productField.field.name] = (
                  isWidgetpage
                    ? productField.required_for_online_booking
                    : productField.required_for_internal_booking
                )
                  ? productField.field.name === "email"
                    ? Yup.string()
                        .email("*Invalid email")
                        .required(`*${productField.label} Required`)
                    : Yup.string().required(`*${productField.label} Required`)
                  : Yup.string()
              }
            }
          })

          return Yup.object().shape({
            internalNotes: Yup.string(),
            participantsDetails: hideParticipantFields
              ? Yup.array()
              : Yup.array().of(Yup.object().shape(perParticipantFields)),
            perBookingFieldsDetails: hidePerBookingFields
              ? Yup.array()
              : Yup.array().of(Yup.object().shape(perBookingFields)),
          })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    onSubmit,
    enableReinitialize: true,
    innerRef: participantDetailsRef,
  })
  const { getFieldProps, errors, values, touched, isValid, resetForm, setFieldValue } =
    participationDetailsForm

  const checkAddOnCapacity = async ({
    key,
    unit,
    addOnindex,
    participantIndex,
    index,
    updatedValue,
    addon,
  }: any) => {
    // Below logic is for adding the old_quantity property when editing a cart item
    const addonCartItemDetails = formInitialValues?.addonDetails?.find(
      (addOn) => addOn.addonId === addon?.id
    )
    let addonPayload
    if (addonCartItemDetails?.availability === "customer") {
      const addonCartItemUnitDetails = addonCartItemDetails?.units?.find(
        (unitInfo) => unitInfo?.unitId === unit?.id
      )
      addonPayload = {
        cartItemId: addonCartItemUnitDetails?.id,
        old_quantity: addonCartItemUnitDetails?.unitCount,
      }
    } else if (addonCartItemDetails?.availability === "participant" && participantIndex) {
      const addonCartItemParticipantDetails = addonCartItemDetails?.units?.find(
        (addOn) => addOn?.participantId === participantIndex && addOn?.unitId === unit?.id
      )
      addonPayload = {
        cartItemId: addonCartItemParticipantDetails?.id,
        old_quantity: addonCartItemParticipantDetails?.unitCount,
      }
    }

    const payload = {
      quantities: [
        {
          unit_id: unit?.id,
          quantity: updatedValue || 0,
          old_quantity: addonPayload?.old_quantity ?? 0,
          cartItemId: addonPayload?.cartItemId,
        },
      ],
    }
    try {
      const { data } = await ProductPricingServices.getMerchandisePricingByQuantities(
        addon?.id,
        payload,
        false
      )

      setAddOnPrices((oldData) => {
        return {
          ...oldData,
          [`${addon?.id}-${unit?.id}-${participantIndex}`]: data[0]?.formatted_price ?? 0,
        }
      })
    } catch (e) {
      Sentry.captureException(e)
      console.log({ e })
    }
  }

  useEffect(() => {
    if (Object.keys(debounced).length !== 0) {
      checkAddOnCapacity(debounced)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  if (isProductFieldsDataLoading || isProductAddonsDataLoading)
    return (
      <div className={"py-5 text-center"}>
        <Spinner size={"md"} light />
      </div>
    )

  return (productFieldsData || productFieldsDataError) && initialValues ? (
    <div className={"flex flex-col items-start gap-3 sm:pt-6 pt-3"}>
      {alertMessage ? (
        <div
          className={`
          flex flex-row items-start justify-between gap-2 p-3 border-y mb-1 mx-6 border-orange-500 bg-orange-200
        `}
        >
          <div className={"flex flex-row items-start gap-2 justify-start"}>
            <InfoIcon className={"w-5 h-5"} />
            <div className={"text14Medium text-neutral-1000"}>{alertMessage}</div>
          </div>
        </div>
      ) : null}
      <div className={"w-full"}>
        <FormikProvider value={participationDetailsForm}>
          <Form id={"participantsDetailsForm"}>
            <div className={"flex flex-col w-full"}>
              <div
                className={`px-6 sm:pr-2 ${
                  error ? "sm:pb-4" : "sm:pb-8"
                } flex flex-col items-center justify-start w-full gap-6`}
              >
                {/* Per Participant fields */}
                {!(
                  !values.participantsDetails?.length ||
                  !productFieldsData?.filter(
                    (field) =>
                      field.ask?.includes(ASK_PER_PARTICIPANT) &&
                      (isWidgetpage ? field.allowed_online_booking : field.allowed_internal_booking)
                  ).length
                ) && !hideParticipantFields ? (
                  <div className={"flex flex-col items-start justify-start w-full"}>
                    <h4 className={"text14Medium text-neutral-1000"}>Add Participant Details</h4>
                    <div className={"bt-px w-full mt-1 mb-4 border border-neutral-200"} />
                    <FieldArray {...getFieldProps("participantsDetails")}>
                      {() => {
                        return (
                          <div className={"flex flex-col items-start justify-start w-full gap-4"}>
                            {values.participantsDetails?.map((fieldGroup, index) => (
                              <Accordion
                                alwaysOpen
                                arrowIcon={() => (
                                  <DownIcon width={14} height={14} className={"text-neutral-500"} />
                                )}
                                key={index}
                                collapseAll={
                                  typeof focusedParticipantId !== "undefined"
                                    ? fieldGroup.id !== focusedParticipantId
                                    : false
                                }
                                className={
                                  "w-full rounded-md border border-neutral-100 bg-whiteBlack-100 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)]"
                                }
                              >
                                <Accordion.Panel>
                                  <Accordion.Title
                                    className={
                                      "relative !justify-between !bg-whiteBlack-100 !px-4 outline-none !ring-0"
                                    }
                                  >
                                    <span className={"text14Semibold text-neutral-900"}>
                                      {fieldGroup.participantGroupName}
                                    </span>
                                  </Accordion.Title>
                                  <Accordion.Content
                                    className={"relative -mt-2 !border-0 !px-4 !pt-0"}
                                  >
                                    <div className={"sm:grid sm:grid-cols-2 gap-4"}>
                                      <ParticipantsFields
                                        fieldsData={
                                          productFieldsData.filter(
                                            (field) =>
                                              field.ask?.includes(ASK_PER_PARTICIPANT) &&
                                              (isWidgetpage
                                                ? field.allowed_online_booking
                                                : field.allowed_internal_booking) &&
                                              checkDisplayRules({
                                                field,
                                                formValues: fieldGroup,
                                                ticketInfo: productInfo?.units,
                                                isDontShowForBookingEnabled: !isPostOrder,
                                              }) === "show"
                                          ) ?? []
                                        }
                                        fieldGroupFormName={"participantsDetails"}
                                        fieldArrayIndex={index}
                                        isWidgetPage={isWidgetpage}
                                      />
                                    </div>
                                  </Accordion.Content>
                                </Accordion.Panel>
                              </Accordion>
                            ))}
                          </div>
                        )
                      }}
                    </FieldArray>
                  </div>
                ) : !productFieldsData.filter(
                    (field) =>
                      field.ask?.includes(ASK_PER_PARTICIPANT) &&
                      (isWidgetpage ? field.allowed_online_booking : field.allowed_internal_booking)
                  ).length &&
                  hideAddonsFields &&
                  hidePerBookingFields ? (
                  <EmptyItems title={"No Participants fields found!"} />
                ) : null}

                {/* Per Booking fields */}
                {!(
                  !values.perBookingFieldsDetails?.length ||
                  !productFieldsData.filter(
                    (field) =>
                      field.ask?.includes(ASK_PER_BOOKING) &&
                      (isWidgetpage ? field.allowed_online_booking : field.allowed_internal_booking)
                  ).length
                ) && !hidePerBookingFields ? (
                  <div className={"flex flex-col items-start justify-start w-full"}>
                    <h4 className={"text14Medium text-neutral-1000"}>Add Booking Details</h4>
                    <div className={"bt-px w-full mt-1 mb-4 border border-neutral-200"} />
                    <FieldArray {...getFieldProps("perBookingFieldsDetails")}>
                      {() => {
                        if (!values.perBookingFieldsDetails?.length) return
                        return (
                          <>
                            {values.perBookingFieldsDetails.map(
                              (perBookingFieldsDetails, index) => (
                                <div
                                  className={
                                    "sm:grid grid-cols-2 gap-4 w-full bg-whiteBlack-100 rounded-md px-5 py-5"
                                  }
                                  key={index}
                                >
                                  <ParticipantsFields
                                    fieldsData={
                                      productFieldsData.filter(
                                        (field) =>
                                          field.ask?.includes(ASK_PER_BOOKING) &&
                                          (isWidgetpage
                                            ? field.allowed_online_booking
                                            : field.allowed_internal_booking) &&
                                          checkDisplayRules({
                                            field,
                                            formValues: perBookingFieldsDetails,
                                            ticketInfo: productInfo?.units,
                                            isDontShowForBookingEnabled: !isPostOrder,
                                          }) === "show"
                                      ) ?? []
                                    }
                                    fieldGroupFormName={"perBookingFieldsDetails"}
                                    fieldArrayIndex={0}
                                    type={ASK_PER_BOOKING}
                                  />
                                </div>
                              )
                            )}
                          </>
                        )
                      }}
                    </FieldArray>
                  </div>
                ) : !productFieldsData.filter(
                    (field) =>
                      field.ask?.includes(ASK_PER_BOOKING) &&
                      (isWidgetpage ? field.allowed_online_booking : field.allowed_internal_booking)
                  ).length &&
                  hideAddonsFields &&
                  hideParticipantFields ? (
                  <EmptyItems title={"No Booking fields found!"} />
                ) : null}

                {/* Addons details */}
                {(productAddonsData as unknown as ProductAddonType[]).filter((addon) =>
                  checkAddonDisplayRules({
                    addon,
                    skipAddonDisplayRules,
                    isDontShowForBookingEnabled: !isPostOrder,
                    perBookingFields: values?.perBookingFieldsDetails?.[0],
                    perParticipantFields: values.participantsDetails,
                    ticketInfo: productInfo?.units,
                  })
                )?.length &&
                Object.keys(productAddonsPriceData ?? {}).length &&
                !hideAddonsFields ? (
                  <div className={"flex flex-col items-start justify-start w-full"}>
                    <h4 className={"text14Medium text-neutral-1000"}>Add-ons</h4>
                    <div className={"bt-px w-full mt-1 mb-4 border border-neutral-200"} />
                    <div className={"flex flex-col items-start justify-start gap-4 w-full"}>
                      {(productAddonsData as unknown as ProductAddonType[])
                        .filter((addon) =>
                          checkAddonDisplayRules({
                            addon,
                            skipAddonDisplayRules,
                            isDontShowForBookingEnabled: !isPostOrder,
                            perBookingFields: values?.perBookingFieldsDetails?.[0],
                            perParticipantFields: values.participantsDetails,
                            ticketInfo: productInfo?.units,
                          })
                        )
                        .map((addon, addOnindex) => {
                          return (
                            <div
                              key={addOnindex}
                              className={
                                "w-full pt-4 pb-5 gap-3.5 flex flex-col items-start bg-whiteBlack-100 rounded-md border border-neutral-100 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)]"
                              }
                            >
                              {values.addonDetails?.filter((ad) => ad.addonId === addon.id)?.[0]
                                ?.error && (
                                <p
                                  className={
                                    "paragraph14Medium text-red-600 mx-5 whitespace-pre-line"
                                  }
                                >
                                  {
                                    values.addonDetails.filter((ad) => ad.addonId === addon.id)[0]
                                      .error
                                  }
                                </p>
                              )}
                              <div className={"flex flex-col gap-2 px-5"}>
                                <div className={"flex flex-row items-center justify-start gap-1"}>
                                  <p className={"text14Semibold text-neutral-900"}>{addon.name}</p>
                                  {addon.merchandise.requirements === "required" ? (
                                    <Badge color={"red"} className={"ml-2"} size={"xs"}>
                                      Mandatory
                                    </Badge>
                                  ) : null}
                                </div>
                                {addon.description ? (
                                  <div
                                    className={"paragraph14Regular text-neutral-600"}
                                    dangerouslySetInnerHTML={{
                                      __html: addon?.description?.toString() ?? "",
                                    }}
                                  />
                                ) : null}
                              </div>
                              {(addon.merchandise.availability === "participant"
                                ? values.participantsDetails
                                : Array(1).fill(0)
                              )
                                ?.filter((participant) =>
                                  addon.merchandise.availability === "customer"
                                    ? true
                                    : checkDisplayRules({
                                        field: addon as unknown as ProductFieldType,
                                        formValues: participant,
                                        ticketInfo: productInfo?.units,
                                        isDontShowForBookingEnabled: !isPostOrder,
                                      }) === "show"
                                )
                                ?.map((participant, participantIndex) => {
                                  const uniqparticipantId =
                                    addon.merchandise.availability === "participant"
                                      ? (participant?.id ?? participantIndex)
                                      : undefined
                                  return (
                                    <div
                                      key={`${uniqparticipantId}-${participantIndex}`}
                                      className={`
                                        w-full flex gap-4 flex-row items-start justify-between px-5
                                        ${
                                          addon.merchandise.availability === "participant"
                                            ? "pt-3.5 border-t border-neutral-200 gap-8"
                                            : ""
                                        }
                                      `}
                                    >
                                      {addon.merchandise.availability === "participant" &&
                                      participant?.participantGroupName ? (
                                        <p className={"shrink-0"}>
                                          {participant.participantGroupName}
                                        </p>
                                      ) : null}
                                      <div
                                        className={`flex flex-col items-start justify-start gap-3 ${
                                          addon.merchandise.availability === "customer"
                                            ? "w-full"
                                            : ""
                                        }`}
                                      >
                                        {productAddonsPriceData?.[addon.id]?.length &&
                                          productAddonsPriceData?.[addon.id].map(
                                            (unitInfo, index) => {
                                              let targetAddonUnitIndex: number
                                              const targetAddonIndex =
                                                values.addonDetails?.findIndex((value) => {
                                                  let isTargetAddonIndexFound = false
                                                  if (value.addonId === addon.id) {
                                                    isTargetAddonIndexFound = true
                                                    value.units?.every((unit, index) => {
                                                      if (
                                                        addon.merchandise.availability ===
                                                          "participant" &&
                                                        ((unit?.participantId &&
                                                          participant?.id &&
                                                          participant.id === unit.participantId) ||
                                                          (unit?.participantGroupName ===
                                                            participant?.participantGroupName &&
                                                            unit?.participantUnitId ===
                                                              participant?.participantUnitId)) &&
                                                        unit?.unitId === unitInfo.unit?.id
                                                      ) {
                                                        targetAddonUnitIndex = index
                                                        return false
                                                      } else if (
                                                        addon.merchandise.availability ===
                                                          "customer" &&
                                                        unit?.unitId === unitInfo.unit?.id
                                                      ) {
                                                        targetAddonUnitIndex = index
                                                        return false
                                                      }
                                                      return true
                                                    })
                                                  } else {
                                                    isTargetAddonIndexFound = false
                                                  }
                                                  return isTargetAddonIndexFound
                                                })
                                              const unitAddOnKey = `addonDetails.${targetAddonIndex}.units.${targetAddonUnitIndex}.unitCount`
                                              return (
                                                <div
                                                  key={index}
                                                  className={
                                                    "flex flex-row items-center justify-between gap-2 w-full"
                                                  }
                                                >
                                                  <div className={`flex items-center`}>
                                                    <p
                                                      className={
                                                        "mr-3 text14Medium text-neutral-900"
                                                      }
                                                    >
                                                      {unitInfo.unit.name}
                                                    </p>
                                                    <div className={"flex items-center gap-3"}>
                                                      <button
                                                        type={"button"}
                                                        tabIndex={-1}
                                                        onClick={() => {
                                                          if (targetAddonIndex >= 0) {
                                                            if (
                                                              typeof targetAddonUnitIndex ===
                                                              "number"
                                                            ) {
                                                              const updatedValue =
                                                                (values?.addonDetails?.[
                                                                  targetAddonIndex
                                                                ]?.units?.[targetAddonUnitIndex]
                                                                  ?.unitCount ?? 1) - 1
                                                              setFieldValue(
                                                                unitAddOnKey,
                                                                updatedValue
                                                              )
                                                              setDebounceParams({
                                                                key: unitAddOnKey,
                                                                unit: unitInfo?.unit,
                                                                addOnindex: addon?.id,
                                                                participantIndex: uniqparticipantId,
                                                                index,
                                                                updatedValue,
                                                                addon,
                                                              })
                                                            }
                                                          }
                                                        }}
                                                        disabled={
                                                          values?.addonDetails?.[targetAddonIndex]
                                                            ?.units?.[targetAddonUnitIndex]
                                                            ?.unitCount
                                                            ? values.addonDetails[targetAddonIndex]
                                                                .units[targetAddonUnitIndex]
                                                                .unitCount === 0
                                                            : true
                                                        }
                                                      >
                                                        <span
                                                          className={
                                                            "m-auto block h-[30px] w-[30px] rounded-full bg-neutral-200 text-xl text-black"
                                                          }
                                                        >
                                                          −
                                                        </span>
                                                      </button>
                                                      <div
                                                        className={
                                                          "relative flex h-[29px] w-[40px] items-center justify-center border-b border-neutral-300 px-1 py-1.5"
                                                        }
                                                      >
                                                        <input
                                                          value={`${parseInt(`${values?.addonDetails?.[targetAddonIndex]?.units?.[targetAddonUnitIndex]?.unitCount ?? 0}`, 10)}`}
                                                          onChange={(e) => {
                                                            if (
                                                              e.target.valueAsNumber >
                                                              addon.merchandise
                                                                .maximum_purchasable_quantity
                                                            ) {
                                                              showToast({
                                                                heading: `Maximum Purchasable Quantity: ${addon.merchandise.maximum_purchasable_quantity}`,
                                                                type: "danger",
                                                              })
                                                            } else {
                                                              if (targetAddonIndex >= 0) {
                                                                if (
                                                                  typeof targetAddonUnitIndex ===
                                                                  "number"
                                                                ) {
                                                                  const updatedValue = e.target
                                                                    ?.valueAsNumber
                                                                    ? e.target.valueAsNumber
                                                                    : 0
                                                                  setFieldValue(
                                                                    unitAddOnKey,
                                                                    updatedValue
                                                                  )
                                                                  setDebounceParams({
                                                                    key: unitAddOnKey,
                                                                    unit: unitInfo?.unit,
                                                                    addOnindex: addon?.id,
                                                                    participantIndex:
                                                                      uniqparticipantId,
                                                                    index,
                                                                    updatedValue,
                                                                    addon,
                                                                  })
                                                                } else {
                                                                  setFieldValue(
                                                                    `addonDetails.${targetAddonIndex}.units`,
                                                                    [
                                                                      ...values?.addonDetails?.[
                                                                        targetAddonIndex
                                                                      ].units,
                                                                      {
                                                                        unitCount: e.target
                                                                          ?.valueAsNumber
                                                                          ? e.target.valueAsNumber
                                                                          : 0,
                                                                        participantGroupName:
                                                                          participant?.participantGroupName,
                                                                        participantUnitId:
                                                                          participant?.participantUnitId,
                                                                        unitId: unitInfo.unit.id,
                                                                        unitName:
                                                                          unitInfo.unit.name,
                                                                        participantId:
                                                                          participant?.id,
                                                                      },
                                                                    ]
                                                                  )
                                                                  setDebounceParams({
                                                                    key: unitAddOnKey,
                                                                    unit: unitInfo?.unit,
                                                                    addOnindex: addon?.id,
                                                                    participantIndex:
                                                                      uniqparticipantId,
                                                                    index,
                                                                    updatedValue: e.target
                                                                      ?.valueAsNumber
                                                                      ? e.target.valueAsNumber
                                                                      : 0,
                                                                    addon,
                                                                  })
                                                                }
                                                              } else {
                                                                setFieldValue("addonDetails", [
                                                                  ...values.addonDetails,
                                                                  {
                                                                    addonId: addon.id,
                                                                    error: "",
                                                                    availability:
                                                                      addon.merchandise
                                                                        .availability,
                                                                    requirement:
                                                                      addon.merchandise
                                                                        .requirements,
                                                                    units: [
                                                                      {
                                                                        unitCount: e.target
                                                                          ?.valueAsNumber
                                                                          ? e.target.valueAsNumber
                                                                          : 0,
                                                                        participantGroupName:
                                                                          participant?.participantGroupName,
                                                                        participantUnitId:
                                                                          participant?.participantUnitId,
                                                                        unitId: unitInfo.unit.id,
                                                                        unitName:
                                                                          unitInfo.unit.name,
                                                                        participantId:
                                                                          participant?.id,
                                                                      },
                                                                    ],
                                                                  } as addonDetailsType,
                                                                ])
                                                                setDebounceParams({
                                                                  key: unitAddOnKey,
                                                                  unit: unitInfo?.unit,
                                                                  addOnindex: addon?.id,
                                                                  participantIndex:
                                                                    uniqparticipantId,
                                                                  index,
                                                                  updatedValue: e.target
                                                                    ?.valueAsNumber
                                                                    ? e.target.valueAsNumber
                                                                    : 0,
                                                                  addon,
                                                                })
                                                              }
                                                            }
                                                          }}
                                                          type={"number"}
                                                          className={
                                                            "absolute px-0 !ring-0 text-center pb-1 text14Regular text-neutral-700 w-full border-none bg-transparent"
                                                          }
                                                          max={
                                                            addon.merchandise
                                                              .maximum_purchasable_quantity
                                                          }
                                                          min={0}
                                                        />
                                                      </div>
                                                      <button
                                                        type={"button"}
                                                        tabIndex={+1}
                                                        disabled={
                                                          values?.addonDetails?.[targetAddonIndex]
                                                            ?.units?.[targetAddonUnitIndex]
                                                            ?.unitCount
                                                            ? values.addonDetails[targetAddonIndex]
                                                                .units[targetAddonUnitIndex]
                                                                .unitCount >=
                                                              addon.merchandise
                                                                .maximum_purchasable_quantity
                                                            : false
                                                        }
                                                        onClick={() => {
                                                          if (targetAddonIndex >= 0) {
                                                            if (
                                                              typeof targetAddonUnitIndex ===
                                                              "number"
                                                            ) {
                                                              const updatedValue =
                                                                (values?.addonDetails?.[
                                                                  targetAddonIndex
                                                                ]?.units?.[targetAddonUnitIndex]
                                                                  ?.unitCount ?? 1) + 1
                                                              setFieldValue(
                                                                unitAddOnKey,
                                                                updatedValue
                                                              )
                                                              setDebounceParams({
                                                                key: unitAddOnKey,
                                                                unit: unitInfo?.unit,
                                                                addOnindex: addon?.id,
                                                                participantIndex: uniqparticipantId,
                                                                index,
                                                                updatedValue,
                                                                addon,
                                                              })
                                                            } else {
                                                              setFieldValue(
                                                                `addonDetails.${targetAddonIndex}.units`,
                                                                [
                                                                  ...values?.addonDetails?.[
                                                                    targetAddonIndex
                                                                  ].units,
                                                                  {
                                                                    unitCount: 1,
                                                                    participantGroupName:
                                                                      participant?.participantGroupName,
                                                                    participantUnitId:
                                                                      participant?.participantUnitId,
                                                                    unitId: unitInfo.unit.id,
                                                                    unitName: unitInfo.unit.name,
                                                                    participantId: participant?.id,
                                                                  },
                                                                ]
                                                              )
                                                              setDebounceParams({
                                                                key: unitAddOnKey,
                                                                unit: unitInfo?.unit,
                                                                addOnindex: addon?.id,
                                                                participantIndex: uniqparticipantId,
                                                                index,
                                                                updatedValue: 1,
                                                                addon,
                                                              })
                                                            }
                                                          } else {
                                                            setFieldValue("addonDetails", [
                                                              ...values.addonDetails,
                                                              {
                                                                addonId: addon.id,
                                                                error: "",
                                                                availability:
                                                                  addon.merchandise.availability,
                                                                requirement:
                                                                  addon.merchandise.requirements,
                                                                units: [
                                                                  {
                                                                    unitCount: 1,
                                                                    participantGroupName:
                                                                      participant?.participantGroupName,
                                                                    participantUnitId:
                                                                      participant?.participantUnitId,
                                                                    unitId: unitInfo.unit.id,
                                                                    unitName: unitInfo.unit.name,
                                                                    participantId: participant?.id,
                                                                  },
                                                                ],
                                                              } as addonDetailsType,
                                                            ])
                                                            setDebounceParams({
                                                              key: unitAddOnKey,
                                                              unit: unitInfo?.unit,
                                                              addOnindex: addon?.id,
                                                              participantIndex: uniqparticipantId,
                                                              index,
                                                              updatedValue: 1,
                                                              addon,
                                                            })
                                                          }
                                                        }}
                                                      >
                                                        <span
                                                          className={
                                                            "m-auto block h-[30px] w-[30px] rounded-full bg-neutral-200 text-xl text-black"
                                                          }
                                                        >
                                                          +
                                                        </span>
                                                      </button>
                                                    </div>
                                                    {unitInfo.unit?.description ? (
                                                      <p
                                                        className={
                                                          "ml-4 text14Regular text-neutral-900"
                                                        }
                                                      >
                                                        {unitInfo.unit.description}
                                                      </p>
                                                    ) : null}
                                                  </div>
                                                  <p className={"text14Semibold text-primary-600"}>
                                                    {addonPrices?.[
                                                      `${addon?.id}-${unitInfo?.unit?.id}-${uniqparticipantId}`
                                                    ] ?? unitInfo?.formatted_price}
                                                  </p>
                                                </div>
                                              )
                                            }
                                          )}
                                      </div>
                                    </div>
                                  )
                                })}
                            </div>
                          )
                        })}
                    </div>
                  </div>
                ) : !(productAddonsData as unknown as ProductAddonType[])?.filter(
                    (addon) =>
                      skipAddonDisplayRules ||
                      checkDisplayRules({
                        field: addon as unknown as ProductFieldType,
                        formValues: values?.perBookingFieldsDetails[0],
                        ticketInfo: productInfo?.units,
                        isDontShowForBookingEnabled: !isPostOrder,
                      }) === "show"
                  )?.length &&
                  hideParticipantFields &&
                  hidePerBookingFields ? (
                  <EmptyItems title={"No Add-ons found!"} />
                ) : null}

                {!hideDefaultContainer && !hideNoteField && (
                  <div className={"flex flex-col items-start justify-start w-full"}>
                    <h4 className={"text14Medium text-neutral-1000"}>Internal Note</h4>
                    <div className={"bt-px w-full mt-1 mb-4 border border-neutral-200"} />
                    <FormGroup label={""} name={"internalNotes"} className={"w-full"}>
                      <Textarea
                        className={"bg-whiteBlack-100"}
                        {...getFieldProps("internalNotes")}
                        placeholder={"Enter Internal Notes"}
                        rows={4}
                        color={
                          errors.internalNotes && touched.internalNotes ? "failure" : undefined
                        }
                      />
                    </FormGroup>
                  </div>
                )}
              </div>
              {error ? <p className={"mx-6 mb-3 text14Regular text-red-600"}>{error}</p> : null}
              {!hideDefaultContainer && (
                <div
                  className={`
                        flex flex-1 flex-row items-center border-t border-neutral-200 bg-whiteBlack-100 px-6 py-4
                        ${hideBackButton ? "justify-end" : "justify-between"}
                      `}
                >
                  {!hideBackButton && (
                    <Button color={"ghost"} size={"md"} onClick={goToBackStep}>
                      <LeftSmallIcon width={18} height={18} className={"mr-2"} />
                      Back
                    </Button>
                  )}
                  <div className={"flex flex-row items-center gap-3"}>
                    <Button
                      outline
                      className={"border-primary-600 text-primary-600"}
                      size={"md"}
                      onClick={() => {
                        resetForm()
                        handleClose()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size={"md"}
                      type={"submit"}
                      color={"primary"}
                      disabled={!isValid || formSubmitting}
                    >
                      {formSubmitting ? <Spinner size={"sm"} light /> : "Save"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Form>
        </FormikProvider>
      </div>
    </div>
  ) : (
    <div className={"py-5 text-center"}>
      <Spinner size={"md"} light />
    </div>
  )
}

export default ParticipationDetailsForm
